import { useState } from "react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common/page-header";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Sliders, ChevronLeft, ChevronRight } from "lucide-react";
import { ComponentPriceTable } from "@/components/pricing-components/ComponentPriceTable";
import { ComponentPriceForm } from "@/components/pricing-components/ComponentPriceForm";
import { usePricingComponents } from "@/hooks/usePricingComponents";
import { useComponentPrices } from "@/hooks/useComponentPrices";
import type { ComponentPriceListDto } from "@/types/component-price";

const PAGE_SIZE = 20;

export function ComponentPricesPage() {
  // Component selection state
  const [selectedComponentId, setSelectedComponentId] = useState<string>("");
  const [selectedComponentName, setSelectedComponentName] =
    useState<string>("");

  // Pagination state
  const [page, setPage] = useState(1);

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingPriceId, setEditingPriceId] = useState<string | null>(null);

  // Fetch all pricing components for the selector
  const { data: componentsResponse, isLoading: componentsLoading } =
    usePricingComponents({
      isActive: true,
      limit: 100,
    });
  const components = componentsResponse?.data || [];

  // Fetch prices for the selected component
  const {
    data: pricesResponse,
    isLoading: pricesLoading,
    error,
    refetch,
  } = useComponentPrices(selectedComponentId, { page, limit: PAGE_SIZE });
  const prices: ComponentPriceListDto[] = pricesResponse?.data || [];
  const pagination = pricesResponse?.pagination;

  const totalPages = pagination?.totalPages ?? 1;

  const handleComponentSelect = (componentId: string) => {
    const component = components.find((c) => c.id === componentId);
    setSelectedComponentId(componentId);
    setSelectedComponentName(component?.name || "");
    setPage(1);
  };

  const handleEdit = (price: ComponentPriceListDto) => {
    setEditingPriceId(price.id);
    setIsEditModalOpen(true);
  };

  // Error state
  if (error && selectedComponentId) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Component Prices"
          subtitle="Manage prices for pricing components"
        />
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-destructive-ink">
                Failed to load component prices
              </p>
              <Button onClick={() => refetch()} className="mt-4">
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Component Prices"
        subtitle="Manage prices for pricing components"
        actions={
          selectedComponentId
            ? [
                {
                  label: "Add Price",
                  onClick: () => setIsCreateModalOpen(true),
                  icon: <Plus className="mr-2 h-4 w-4" />,
                },
              ]
            : []
        }
      />

      {/* Component Selector */}
      <Card>
        <CardHeader>
          <CardTitle>Select Component</CardTitle>
          <CardDescription>
            Choose a pricing component to view and manage its prices
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Select
            value={selectedComponentId}
            onValueChange={handleComponentSelect}
          >
            <SelectTrigger className="w-full md:w-[400px]">
              <SelectValue
                placeholder={
                  componentsLoading
                    ? "Loading..."
                    : "Select a pricing component"
                }
              />
            </SelectTrigger>
            <SelectContent>
              {components.map((component) => (
                <SelectItem key={component.id} value={component.id}>
                  {component.name} ({component.slug})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Prices Table */}
      {selectedComponentId ? (
        <Card>
          <CardHeader>
            <CardTitle>Prices for {selectedComponentName}</CardTitle>
            <CardDescription>
              {pagination
                ? `${pagination.totalItems} price${pagination.totalItems !== 1 ? "s" : ""} configured`
                : "Loading prices..."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ComponentPriceTable
              prices={prices}
              isLoading={pricesLoading}
              onEdit={handleEdit}
            />
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={page >= totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-16">
            <div className="text-center">
              <Sliders className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <p className="mt-4 text-muted-foreground">
                Select a pricing component to view its prices
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Price Modal */}
      <ComponentPriceForm
        key="create"
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        componentId={selectedComponentId}
        componentName={selectedComponentName}
        mode="create"
      />

      {/* Edit Price Modal */}
      <ComponentPriceForm
        key={editingPriceId ?? "edit"}
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        componentId={selectedComponentId}
        componentName={selectedComponentName}
        priceId={editingPriceId ?? undefined}
        mode="edit"
      />
    </div>
  );
}
